// Dynamic-source binding resolver. Block settings can be bound to
// page inputs, metafields, resource fields, or expressions. The editor
// shows a lightning-bolt UI next to each compatible setting; the runtime
// resolves bindings before passing values to block components.

import type { Block, BindingExpr, BlockBindings, FilterValueRef } from '@twasta/page-schema'

/** Resolve a `FilterValueRef` (the "fixed value OR page variable" shape used
 * by embed filters, arg mappings and the file-upload block) to a concrete
 * value. Mirrors the designer's livepreview/bindingResolver.resolveRef so both
 * the in-app preview and the deployed runtime resolve identically. */
export function resolveValueRef(
  ref: FilterValueRef | undefined,
  ctx: { vars?: Record<string, unknown> },
): unknown {
  if (!ref) return undefined
  if (ref.source === 'static') return ref.value ?? ''
  if (ref.source === 'page_input' && ref.name) return ctx.vars?.[ref.name]
  return undefined
}

export interface ResolveScope {
  alias?: string
  row: Record<string, unknown>
}

export interface ResolveContext {
  inputs?: Record<string, unknown>            // page-level input values
  metafields?: Record<string, unknown>        // tenant-defined custom properties
  resources?: Record<string, unknown>         // resource_field: `${obj_type}:${obj_name}`; data_field: `ds:<id>`
  vars?: Record<string, unknown>              // ad-hoc vars (action state etc.)
  scopes?: ResolveScope[]                      // repeater row scopes, innermost LAST
}

/** Pick a row scope from the chain: named alias if given, else innermost. */
function pickScope(scopes: ResolveScope[] | undefined, alias?: string): ResolveScope | undefined {
  if (!scopes || scopes.length === 0) return undefined
  if (alias) {
    for (let i = scopes.length - 1; i >= 0; i--) {
      if (scopes[i].alias === alias) return scopes[i]
    }
    return undefined
  }
  return scopes[scopes.length - 1]
}

/**
 * Resolve a single binding expression to a concrete value. Returns the
 * fallback (or undefined) when the source can't be resolved.
 */
export function resolveBinding(b: BindingExpr, ctx: ResolveContext): unknown {
  const v = resolveRaw(b, ctx)
  return b.map ? mapRows(v, b.map, b.fallback) : v
}

/** `BindingExpr.map`: rows → items (`{itemKey: COLUMN}`), each with an `id`. */
function mapRows(v: unknown, map: Record<string, string>, fallback: unknown): unknown {
  if (!Array.isArray(v)) return fallback
  return v.map((row, i) => {
    const item: Record<string, unknown> = {}
    for (const [key, column] of Object.entries(map)) {
      const val = readPath(row, column)
      if (val !== undefined && val !== null && val !== '') item[key] = val
    }
    if (item.id === undefined) item.id = String(i)
    return item
  })
}

function resolveRaw(b: BindingExpr, ctx: ResolveContext): unknown {
  try {
    switch (b.source) {
      case 'static':
        return b.value
      case 'page_input':
        return b.name ? ctx.inputs?.[b.name] ?? b.fallback : b.fallback
      case 'metafield':
        return b.name ? ctx.metafields?.[b.name] ?? b.fallback : b.fallback
      case 'resource_field': {
        if (!b.resource || !b.path) return b.fallback
        const key = `${b.resource.obj_type}:${b.resource.obj_name}`
        const root = ctx.resources?.[key]
        return readPath(root, b.path) ?? b.fallback
      }
      case 'data_field': {
        if (!b.dataSource) return b.fallback
        const root = ctx.resources?.[`ds:${b.dataSource}`]
        if (!b.path) return root ?? b.fallback
        // A resolved entry wraps a single record under `row` (record mode).
        // Resolve the path as authored first; if that misses, retry against
        // the wrapped row so a binding can use the bare column name
        // (e.g. `EVENT_NAME`) instead of `row.EVENT_NAME`.
        let v = readPath(root, b.path)
        if (v === undefined && root && typeof root === 'object' && 'row' in (root as Record<string, unknown>)) {
          v = readPath((root as Record<string, unknown>).row, b.path)
        }
        return v ?? b.fallback
      }
      case 'item_field': {
        const scope = pickScope(ctx.scopes, b.scope)
        if (!scope) return b.fallback
        const key = b.path || b.name || ''
        return readPath(scope.row, key) ?? b.fallback
      }
      case 'expression': {
        if (!b.expression) return b.fallback
        const subbed = substitute(b.expression, ctx)
        return safeEval(subbed) ?? b.fallback
      }
    }
  } catch {
    return b.fallback
  }
}

/**
 * Apply all bindings on a block. Returns a shallow clone with bound
 * setting paths overwritten. Path syntax: dotted, e.g. 'card.title' or
 * 'tiers.0.price'.
 */
export function applyBindings<T extends Block>(block: T, ctx: ResolveContext): T {
  const bindings: BlockBindings | undefined = block.bindings
  if (!bindings || Object.keys(bindings).length === 0) return block
  const cloned = JSON.parse(JSON.stringify(block)) as T
  for (const [path, expr] of Object.entries(bindings)) {
    const value = resolveBinding(expr, ctx)
    if (value !== undefined) writePath(cloned as unknown as Record<string, unknown>, path, value)
  }
  return cloned
}

function readPath(root: unknown, path: string): unknown {
  if (root == null) return undefined
  return path.split('.').reduce<any>((acc, key) => {
    if (acc == null) return acc
    if (Array.isArray(acc)) return acc[Number(key)]
    if (typeof acc !== 'object') return undefined
    if (key in acc) return acc[key]
    // Case-insensitive fallback: bindings are authored with metadata
    // column names (e.g. ARTIST_NAME) while resolved rows are keyed by the
    // executor's lowercased form key (artist_name). Match either way.
    const lk = key.toLowerCase()
    for (const k of Object.keys(acc)) {
      if (k.toLowerCase() === lk) return acc[k]
    }
    return undefined
  }, root)
}

function writePath(root: Record<string, unknown>, path: string, value: unknown): void {
  const parts = path.split('.')
  let cur: any = root
  for (let i = 0; i < parts.length - 1; i++) {
    const k = parts[i]
    if (cur[k] == null || typeof cur[k] !== 'object') cur[k] = {}
    cur = cur[k]
  }
  cur[parts[parts.length - 1]] = value
}

function substitute(expr: string, ctx: ResolveContext): string {
  const lookup = (key: string): unknown => {
    const segments = key.split('.')
    const head = segments[0]
    const rest = segments.slice(1).join('.')
    if (head === 'inputs') return readPath(ctx.inputs, rest)
    if (head === 'metafields') return readPath(ctx.metafields, rest)
    if (head === 'vars') return readPath(ctx.vars, rest)
    const direct = ctx.inputs?.[key] ?? ctx.vars?.[key]
    if (direct !== undefined) return direct
    // `{{<dataSourceId>.<COLUMN>}}` — the record a page data source resolved
    // (first row of a list one); `._loaded` says whether it has arrived. The
    // same tokens page text, visibility conditions and actions read.
    const entry = rest ? ctx.resources?.[`ds:${head}`] as { row?: unknown; rows?: unknown[] } | undefined : undefined
    if (entry !== undefined || (rest === '_loaded' && ctx.resources)) {
      if (rest === '_loaded') return entry ? 'true' : 'false'
      const row = entry?.row ?? (Array.isArray(entry?.rows) ? entry?.rows[0] : undefined)
      return readPath(row, rest) ?? ''
    }
    return undefined
  }
  return expr.replace(/\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g, (_, key: string) => {
    const v = lookup(key)
    if (v == null) return 'null'
    if (typeof v === 'string') return JSON.stringify(v)
    return String(v)
  })
}

// Tiny safe evaluator for expression bindings. Whitelist-only.
function safeEval(src: string): unknown {
  if (!/^[\s0-9"'.\-+*/%<>=!&|()\[\]_a-zA-Z,nul]+$/.test(src)) return undefined
  try {
    // eslint-disable-next-line no-new-func
    return new Function(`return (${src});`)()
  } catch {
    return undefined
  }
}
